import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { labelsApi } from '../api/client';
import { labelExtension, normalizeLabelFormatHint } from '../utils/labels';

interface LoadedLabel {
  objectUrl: string;
  contentType: string;
}

function PrintPage() {
  const [searchParams] = useSearchParams();
  const sourceUrl = searchParams.get('src');
  const labelFormat = normalizeLabelFormatHint(searchParams.get('format'));
  const [label, setLabel] = useState<LoadedLabel | null>(null);
  const [labelLoaded, setLabelLoaded] = useState(false);
  const [loading, setLoading] = useState(Boolean(sourceUrl));
  const [loadError, setLoadError] = useState('');
  const autoPrintedRef = useRef(false);
  const printTimerRef = useRef<number | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setLabel(null);
    setLabelLoaded(false);
    setLoadError('');
    setLoading(Boolean(sourceUrl));
    autoPrintedRef.current = false;

    if (!sourceUrl) return () => controller.abort();

    labelsApi.fetch(sourceUrl, controller.signal)
      .then(({ blob, contentType }) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setLabel({ objectUrl, contentType });
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setLoadError(err instanceof Error ? err.message : 'The label could not be loaded.');
        setLoading(false);
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (printTimerRef.current !== null) window.clearTimeout(printTimerRef.current);
    };
  }, [sourceUrl]);

  const isPdf = label?.contentType === 'application/pdf';
  const isRawPrinterFile = label
    ? ['text/plain', 'application/octet-stream'].includes(label.contentType)
    : false;

  // For PDFs the label renders inside an iframe, and printing the parent
  // document prints a blank page in many browsers (the embedded PDF viewer
  // does not render into the parent's print output) — so print via the iframe
  // itself, falling back to the parent window if the viewer blocks it.
  const printLabel = () => {
    if (isPdf) {
      try {
        const frameWindow = iframeRef.current?.contentWindow;
        if (frameWindow) {
          frameWindow.focus();
          frameWindow.print();
          return;
        }
      } catch {
        // Fall through to printing the parent document.
      }
    }
    window.print();
  };

  const handleLabelLoad = () => {
    setLabelLoaded(true);
    setLoadError('');
    if (autoPrintedRef.current || isRawPrinterFile) return;
    autoPrintedRef.current = true;
    printTimerRef.current = window.setTimeout(() => printLabel(), 100);
  };

  if (!sourceUrl) {
    return (
      <div className="print-container">
        <div className="panel" style={{ textAlign: 'center', padding: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>No Label Source</h2>
          <p style={{ marginBottom: '24px', color: 'var(--aged-mahogany-light)' }}>
            The label URL is missing. Please try creating a new shipment.
          </p>
          <Link to="/" className="btn btn-primary">Create Shipment</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="print-container">
      <div className="print-actions" style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          onClick={printLabel}
          disabled={!labelLoaded || isRawPrinterFile}
        >
          Print Label
        </button>
        {label && (
          <a
            href={label.objectUrl}
            download={`shipping-label.${labelExtension(label.contentType, labelFormat)}`}
            className="btn btn-secondary"
          >
            Download
          </a>
        )}
        <Link to="/" className="btn btn-ghost">Back to Home</Link>
      </div>

      {loading && (
        <div className="panel" role="status" style={{ textAlign: 'center', padding: '40px' }}>
          Loading label securely…
        </div>
      )}

      {!loading && loadError && (
        <div className="panel" role="alert" style={{ textAlign: 'center', padding: '40px' }}>
          <h2 style={{ marginBottom: '12px' }}>Label Unavailable</h2>
          <p style={{ color: 'var(--error-text)' }}>{loadError}</p>
        </div>
      )}

      {label && !loadError && (
        <div className="label-container">
          {isRawPrinterFile ? (
            <div className="panel" style={{ maxWidth: '560px', textAlign: 'center' }}>
              <h2 style={{ marginBottom: '12px' }}>Thermal Printer File</h2>
              <p>This authenticated {labelFormat || 'thermal'} label is printer-language data and cannot be rendered by the browser. Use Download with a compatible thermal printer.</p>
            </div>
          ) : isPdf ? (
            <iframe
              ref={iframeRef}
              src={label.objectUrl}
              title="Shipping Label"
              className="shipping-label"
              onLoad={handleLabelLoad}
              style={{
                width: '8.5in',
                height: '11in',
                border: 'none',
                boxShadow: 'var(--shadow-lg)',
                borderRadius: 'var(--radius-sm)',
              }}
            />
          ) : label.contentType.startsWith('image/') ? (
            <img
              src={label.objectUrl}
              alt="Shipping Label"
              className="shipping-label"
              onLoad={handleLabelLoad}
              onError={() => setLoadError('The authenticated label image could not be rendered.')}
              style={{
                width: '4in',
                height: 'auto',
                maxHeight: '6in',
                boxShadow: 'var(--shadow-lg)',
                borderRadius: 'var(--radius-sm)',
              }}
            />
          ) : (
            <p role="alert">The proxy returned an unsupported label format.</p>
          )}
        </div>
      )}

      <style>{`
        .label-container {
          display: flex;
          justify-content: center;
        }
        @media print {
          @page {
            size: ${isPdf ? '8.5in 11in' : '4in 6in'};
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
          }
          .print-actions {
            display: none !important;
          }
          .print-container {
            padding: 0 !important;
            margin: 0 !important;
          }
          .label-container {
            display: block;
          }
          .shipping-label {
            box-shadow: none !important;
            border-radius: 0 !important;
            width: ${isPdf ? '8.5in' : '4in'} !important;
            height: ${isPdf ? '11in' : '6in'} !important;
            max-width: ${isPdf ? '8.5in' : '4in'} !important;
            max-height: ${isPdf ? '11in' : '6in'} !important;
          }
        }
      `}</style>
    </div>
  );
}

export default PrintPage;
